import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import DataTable from "react-data-table-component";
import "../../wallet.css";
import { Pill } from "./walletShared";

const STATUS_TONE = { OPEN: "warning", IN_PROGRESS: "primary", RESOLVED: "success", CLOSED: "neutral" };
const STATUS_LABEL = { OPEN: "Open", IN_PROGRESS: "In Progress", RESOLVED: "Resolved", CLOSED: "Closed" };
const REPORTER_LABEL = { DRIVER: "Driver", SUPERVISOR: "Supervisor", SYSTEM: "App (auto)" };

/**
 * Support Tickets — IT's queue for issues filed by drivers/supervisors (from InScan, OutScan,
 * BulkScan, Update Status, Bulk Tracking, or the mobile app) plus the driver app's own auto
 * crash reports. Role-gated: visible to 'support', 'admin', 'super_admin' (see Sidebar.js).
 */
export default function SupportTickets() {
  const API_URL = process.env.REACT_APP_API_URL;
  const TICKETS_API = `${API_URL}shypvdriverapp/support-tickets`;

  const userRole = (sessionStorage.getItem("role") || "").toLowerCase();
  const canResolve = ["support", "admin", "super_admin"].includes(userRole);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("");
  const [reporterFilter, setReporterFilter] = useState("");
  const [mode, setMode] = useState("list"); // "list" | "detail"
  const [detailRow, setDetailRow] = useState(null);
  const [resolveStatus, setResolveStatus] = useState("");
  const [resolveNotes, setResolveNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [banner, setBanner] = useState(null);

  const flash = (type, text) => {
    setBanner({ type, text });
    window.clearTimeout(flash._t);
    flash._t = window.setTimeout(() => setBanner(null), 6000);
  };

  const fetchTickets = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set("status", statusFilter);
      if (reporterFilter) params.set("reporterType", reporterFilter);
      const res = await axios.get(`${TICKETS_API}?${params.toString()}`);
      setRows(res.data?.data || []);
    } catch (err) {
      flash("error", "Failed to load tickets: " + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  }, [TICKETS_API, statusFilter, reporterFilter]);

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  const openDetail = async (row) => {
    setDetailRow(row);
    setResolveStatus(row.status || "OPEN");
    setResolveNotes(row.resolutionNotes || "");
    setMode("detail");
    // Re-fetch the single ticket for the freshest detail (stack trace / device info / screenshot).
    try {
      const res = await axios.get(`${TICKETS_API}/${row.id}`);
      if (res.data?.data) {
        setDetailRow(res.data.data);
        setResolveStatus(res.data.data.status || "OPEN");
        setResolveNotes(res.data.data.resolutionNotes || "");
      }
    } catch {
      // fall back to the row we already have
    }
  };

  const closeDetail = () => {
    setMode("list");
    setDetailRow(null);
  };

  const saveResolution = async () => {
    if (!detailRow) return;
    setSaving(true);
    try {
      const resolvedBy = sessionStorage.getItem("user_name") || sessionStorage.getItem("username") || "";
      const res = await axios.patch(`${TICKETS_API}/${detailRow.id}`, {
        status: resolveStatus,
        resolvedBy,
        resolutionNotes: resolveNotes,
      });
      if (res.data?.success !== false) {
        flash("success", "Ticket updated");
        setDetailRow(res.data.data);
        fetchTickets();
      } else {
        flash("error", res.data?.message || "Failed to update ticket");
      }
    } catch (err) {
      flash("error", err.response?.data?.message || err.message);
    } finally {
      setSaving(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        name: "Reporter",
        cell: (r) => (
          <div>
            <div style={{ fontWeight: 600 }}>{REPORTER_LABEL[r.reporterType] || r.reporterType || "—"}</div>
            <div style={{ fontSize: 11.5, color: "#6b7280" }}>{r.reporterName || r.driverId || ""}</div>
          </div>
        ),
        grow: 1.3,
      },
      { name: "Issue type", selector: (r) => r.issueType || (r.errorMessage ? "App Error" : "—"), grow: 1.3 },
      { name: "AWB", selector: (r) => r.awb || "—", grow: 1 },
      { name: "Page", selector: (r) => r.pageContext || "—", width: "130px" },
      {
        name: "Status",
        cell: (r) => <Pill tone={STATUS_TONE[r.status] || "neutral"}>{STATUS_LABEL[r.status] || r.status}</Pill>,
        width: "130px",
      },
      { name: "Reported", selector: (r) => r.createdAt || "—", width: "150px" },
      {
        name: "",
        cell: (r) => (
          <button className="w-btn w-btn-ghost w-btn-sm" onClick={() => openDetail(r)}>View</button>
        ),
        width: "90px",
      },
    ],
    []
  );

  const tableStyles = {
    headRow: { style: { backgroundColor: "#fafbfc", borderBottom: "1px solid #e7eaf0", minHeight: "46px" } },
    headCells: { style: { fontWeight: "700", fontSize: "11.5px", color: "#6b7280", textTransform: "uppercase", letterSpacing: ".4px" } },
    rows: { style: { minHeight: "52px", fontSize: "13.5px" } },
  };

  if (!canResolve) {
    return (
      <div className="wallet-page">
        <div className="w-card">
          <div className="w-card-body" style={{ padding: 32, textAlign: "center", color: "#6b7280" }}>
            Support Tickets is restricted to the support/IT team.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="wallet-page">
      <div className="w-topbar">
        <div className="w-topbar-left">
          <div>
            <h2 className="w-title">Support Tickets</h2>
            <p className="w-subtitle">Issues reported by drivers and supervisors, plus the driver app's own crash reports.</p>
          </div>
        </div>
      </div>

      {banner && <div className={`w-banner ${banner.type === "success" ? "w-banner-success" : "w-banner-error"}`}>{banner.text}</div>}

      {mode === "list" && (
        <div className="w-card">
          <div className="w-card-header">
            <span className="w-card-title">Tickets</span>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <select className="w-input" style={{ height: 34, fontSize: 12.5 }} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                <option value="">All statuses</option>
                <option value="OPEN">Open</option>
                <option value="IN_PROGRESS">In Progress</option>
                <option value="RESOLVED">Resolved</option>
                <option value="CLOSED">Closed</option>
              </select>
              <select className="w-input" style={{ height: 34, fontSize: 12.5 }} value={reporterFilter} onChange={(e) => setReporterFilter(e.target.value)}>
                <option value="">All reporters</option>
                <option value="DRIVER">Driver</option>
                <option value="SUPERVISOR">Supervisor</option>
                <option value="SYSTEM">App (auto)</option>
              </select>
              <button className="w-btn w-btn-ghost w-btn-sm" onClick={fetchTickets}>Refresh</button>
            </div>
          </div>
          <div className="w-card-body">
            <DataTable
              columns={columns}
              data={rows}
              progressPending={loading}
              pagination
              highlightOnHover
              dense
              customStyles={tableStyles}
              noDataComponent={<div style={{ padding: 24, color: "#64748b" }}>No tickets match this filter.</div>}
            />
          </div>
        </div>
      )}

      {mode === "detail" && detailRow && (
        <>
          <div className="w-card-header" style={{ background: "transparent", border: "none", padding: "0 0 14px" }}>
            <span className="w-card-title" style={{ fontSize: 17 }}>
              Ticket #{detailRow.id} — {detailRow.issueType || (detailRow.errorMessage ? "App Error" : "Issue")}
            </span>
            <button className="w-crumb" onClick={closeDetail}>Back to list</button>
          </div>

          <div className="w-card">
            <div className="w-card-body" style={{ paddingTop: 16, display: "flex", flexWrap: "wrap", gap: 24 }}>
              <div>
                <div className="w-field-label">Reporter</div>
                <div style={{ fontSize: 13.5, marginTop: 2 }}>
                  {REPORTER_LABEL[detailRow.reporterType] || detailRow.reporterType} — {detailRow.reporterName || detailRow.driverId || "—"}
                </div>
              </div>
              <div>
                <div className="w-field-label">Page</div>
                <div style={{ fontSize: 13.5, marginTop: 2 }}>{detailRow.pageContext || "—"}</div>
              </div>
              <div>
                <div className="w-field-label">AWB</div>
                <div style={{ fontSize: 13.5, marginTop: 2 }}>{detailRow.awb || "—"}</div>
              </div>
              <div>
                <div className="w-field-label">Reported</div>
                <div style={{ fontSize: 13.5, marginTop: 2 }}>{detailRow.createdAt || "—"}</div>
              </div>
              <div>
                <div className="w-field-label">App version</div>
                <div style={{ fontSize: 13.5, marginTop: 2 }}>{detailRow.appVersion || "—"}</div>
              </div>
            </div>
          </div>

          <div className="w-card">
            <div className="w-card-header"><span className="w-card-title">Description</span></div>
            <div className="w-card-body">
              <p style={{ fontSize: 13.5, whiteSpace: "pre-wrap" }}>{detailRow.context || "—"}</p>
            </div>
          </div>

          {detailRow.errorMessage && (
            <div className="w-card">
              <div className="w-card-header"><span className="w-card-title">Error details</span></div>
              <div className="w-card-body">
                <p style={{ fontSize: 13, color: "#dc2626", fontWeight: 600 }}>{detailRow.errorMessage}</p>
                {detailRow.stackTrace && (
                  <pre style={{ fontSize: 11.5, background: "#f8fafc", padding: 12, borderRadius: 8, overflowX: "auto", maxHeight: 220 }}>
                    {detailRow.stackTrace}
                  </pre>
                )}
                {detailRow.deviceInfoJson && (
                  <>
                    <div className="w-field-label" style={{ marginTop: 12 }}>Device info</div>
                    <pre style={{ fontSize: 11.5, background: "#f8fafc", padding: 12, borderRadius: 8, overflowX: "auto" }}>
                      {detailRow.deviceInfoJson}
                    </pre>
                  </>
                )}
              </div>
            </div>
          )}

          {detailRow.screenshotUrl && (
            <div className="w-card">
              <div className="w-card-header"><span className="w-card-title">Screenshot</span></div>
              <div className="w-card-body">
                <img src={detailRow.screenshotUrl} alt="Ticket screenshot" style={{ maxWidth: "100%", borderRadius: 8, border: "1px solid #e7eaf0" }} />
              </div>
            </div>
          )}

          <div className="w-card">
            <div className="w-card-header"><span className="w-card-title">Resolve</span></div>
            <div className="w-card-body">
              <div className="w-form-row">
                <div className="w-field" style={{ flex: "1 1 220px" }}>
                  <label className="w-field-label">Status</label>
                  <select className="w-input" value={resolveStatus} onChange={(e) => setResolveStatus(e.target.value)}>
                    <option value="OPEN">Open</option>
                    <option value="IN_PROGRESS">In Progress</option>
                    <option value="RESOLVED">Resolved</option>
                    <option value="CLOSED">Closed</option>
                  </select>
                </div>
                {detailRow.resolvedBy && (
                  <div className="w-field" style={{ flex: "1 1 220px" }}>
                    <label className="w-field-label">Last updated by</label>
                    <div style={{ fontSize: 13.5, marginTop: 8 }}>{detailRow.resolvedBy} {detailRow.resolvedAt ? `— ${detailRow.resolvedAt}` : ""}</div>
                  </div>
                )}
              </div>
              <div className="w-field" style={{ marginTop: 12 }}>
                <label className="w-field-label">Resolution notes</label>
                <textarea className="w-textarea" rows={3} value={resolveNotes} onChange={(e) => setResolveNotes(e.target.value)} placeholder="What was done to resolve this?" />
              </div>
              <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 14 }}>
                <button className="w-btn w-btn-primary" onClick={saveResolution} disabled={saving}>
                  {saving ? "Saving…" : "Save"}
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
